import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Rating,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment as ActivityIcon,
  CheckCircle as CompleteIcon,
  ExpandMore as ExpandMoreIcon,
  Flag as GoalIcon,
  Person as SelfIcon,
  Schedule as InProgressIcon,
  SupervisorAccount as ManagerIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../styles/theme';
import {
  getAudienceLabel,
  saveDevelopmentActivity,
  saveDevelopmentGoal,
  saveSelfAppraisal,
  updateDevelopmentGoalProgress
} from '../services/professionalDevelopmentService';

const emptyGoal = {
  title: '',
  description: '',
  priority: 'Medium',
  targetDate: '',
  progress: 0,
  successMeasure: ''
};

const emptyActivity = {
  title: '',
  type: 'Workshop',
  duration: 1,
  date: new Date().toISOString().slice(0, 10),
  reflection: '',
  completed: true
};

const appraisalTemplate = {
  planning: 3,
  practice: 3,
  collaboration: 3,
  impact: 3
};

const ProfessionalDevelopmentGoals = ({ userId, userProfile, record, onDataChanged }) => {
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [appraisalDialogOpen, setAppraisalDialogOpen] = useState(false);
  const [goalForm, setGoalForm] = useState(emptyGoal);
  const [activityForm, setActivityForm] = useState(emptyActivity);
  const [appraisalForm, setAppraisalForm] = useState({
    period: '',
    reflection: '',
    categories: appraisalTemplate
  });
  const [saving, setSaving] = useState(false);

  const audienceLabel = getAudienceLabel(userProfile || {});
  const recommendedGoals = useMemo(() => record?.recommendedGoals || [], [record?.recommendedGoals]);
  const goals = useMemo(() => record?.goals || [], [record?.goals]);
  const activities = useMemo(() => record?.activities || [], [record?.activities]);
  const selfAppraisals = useMemo(() => record?.appraisals?.self || [], [record?.appraisals?.self]);
  const managerAppraisals = useMemo(() => record?.appraisals?.manager || [], [record?.appraisals?.manager]);

  const summary = useMemo(() => ({
    completedGoals: goals.filter((goal) => Number(goal.progress || 0) >= 100).length,
    inProgressGoals: goals.filter((goal) => Number(goal.progress || 0) > 0 && Number(goal.progress || 0) < 100).length,
    completedActivities: activities.filter((activity) => activity.completed).length
  }), [goals, activities]);

  const handleGoalSubmit = async () => {
    if (!userId || !goalForm.title.trim()) return;
    try {
      setSaving(true);
      await saveDevelopmentGoal(userId, goalForm, userProfile || {});
      setGoalDialogOpen(false);
      setGoalForm(emptyGoal);
      await onDataChanged?.();
    } finally {
      setSaving(false);
    }
  };

  const handleActivitySubmit = async () => {
    if (!userId || !activityForm.title.trim()) return;
    try {
      setSaving(true);
      await saveDevelopmentActivity(userId, activityForm, userProfile || {});
      setActivityDialogOpen(false);
      setActivityForm(emptyActivity);
      await onDataChanged?.();
    } finally {
      setSaving(false);
    }
  };

  const handleAppraisalSubmit = async () => {
    if (!userId || !appraisalForm.period.trim()) return;
    try {
      setSaving(true);
      await saveSelfAppraisal(userId, appraisalForm, userProfile || {});
      setAppraisalDialogOpen(false);
      setAppraisalForm({
        period: '',
        reflection: '',
        categories: appraisalTemplate
      });
      await onDataChanged?.();
    } finally {
      setSaving(false);
    }
  };

  const handleProgressUpdate = async (goalId, nextProgress) => {
    if (!userId) return;
    await updateDevelopmentGoalProgress(userId, goalId, nextProgress, userProfile || {});
    await onDataChanged?.();
  };

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Completed goals</Typography>
              <Typography variant="h4" fontWeight={800}>{summary.completedGoals}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Goals in progress</Typography>
              <Typography variant="h4" fontWeight={800}>{summary.inProgressGoals}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Logged activities</Typography>
              <Typography variant="h4" fontWeight={800}>{summary.completedActivities}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
            <Box>
              <Typography variant="h6" fontWeight="bold">Role-aligned priorities</Typography>
              <Typography variant="body2" color="text.secondary">
                Suggested focus areas for your {audienceLabel.toLowerCase()} development.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setGoalDialogOpen(true)} sx={{ bgcolor: THEME_COLORS.primary }}>
              Add Goal
            </Button>
          </Box>

          <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
            {recommendedGoals.map((goal) => (
              <Chip key={goal} label={goal} sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }} />
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
            <Typography variant="h6" fontWeight="bold">Professional development goals</Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setGoalDialogOpen(true)} sx={{ color: THEME_COLORS.primary, borderColor: THEME_COLORS.primary }}>
              Set New Goal
            </Button>
          </Box>

          <Grid container spacing={2}>
            {goals.length === 0 && (
              <Grid item xs={12}>
                <Typography color="text.secondary">No goals recorded yet.</Typography>
              </Grid>
            )}
            {goals.map((goal) => (
              <Grid item xs={12} md={6} key={goal.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} mb={1.5}>
                      <Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <GoalIcon sx={{ color: THEME_COLORS.primary }} />
                          <Typography variant="subtitle1" fontWeight="bold">{goal.title}</Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Due {goal.targetDate || 'not set'}
                        </Typography>
                      </Box>
                      <Chip label={goal.priority} size="small" color={goal.priority === 'High' ? 'error' : goal.priority === 'Low' ? 'success' : 'warning'} />
                    </Box>

                    <Typography variant="body2" sx={{ mb: 2 }}>{goal.description}</Typography>
                    {goal.successMeasure && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Success measure: {goal.successMeasure}
                      </Typography>
                    )}

                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Progress</Typography>
                      <Typography variant="body2" fontWeight="bold">{goal.progress || 0}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={goal.progress || 0}
                      sx={{
                        height: 8,
                        borderRadius: 999,
                        bgcolor: 'rgba(39, 35, 97, 0.08)',
                        '& .MuiLinearProgress-bar': { bgcolor: THEME_COLORS.primary }
                      }}
                    />

                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                      <Button size="small" onClick={() => handleProgressUpdate(goal.id, Math.min(100, Number(goal.progress || 0) + 25))}>
                        Advance
                      </Button>
                      <Button size="small" onClick={() => handleProgressUpdate(goal.id, 100)}>
                        Mark Complete
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
            <Typography variant="h6" fontWeight="bold">Self-directed activities</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setActivityDialogOpen(true)} sx={{ bgcolor: THEME_COLORS.primary }}>
              Log Activity
            </Button>
          </Box>

          <List disablePadding>
            {activities.length === 0 && (
              <Typography color="text.secondary">No activities logged yet.</Typography>
            )}
            {activities.map((activity) => (
              <ListItem key={activity.id} divider disableGutters>
                <ListItemIcon>
                  <ActivityIcon sx={{ color: THEME_COLORS.primary }} />
                </ListItemIcon>
                <ListItemText
                  primary={activity.title}
                  secondary={`${activity.type} • ${activity.duration} hour(s) • ${activity.date}`}
                />
                <Chip
                  icon={activity.completed ? <CompleteIcon /> : <InProgressIcon />}
                  label={activity.completed ? 'Completed' : 'In progress'}
                  size="small"
                  color={activity.completed ? 'success' : 'primary'}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <SelfIcon sx={{ color: THEME_COLORS.primary }} />
                  <Typography variant="h6" fontWeight="bold">Self appraisals</Typography>
                </Box>
                <Button variant="outlined" onClick={() => setAppraisalDialogOpen(true)} sx={{ color: THEME_COLORS.primary, borderColor: THEME_COLORS.primary }}>
                  New appraisal
                </Button>
              </Box>

              {selfAppraisals.length === 0 && (
                <Typography color="text.secondary">No self appraisals submitted yet.</Typography>
              )}
              {selfAppraisals.map((appraisal) => (
                <Accordion key={appraisal.id} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box>
                      <Typography fontWeight="bold">{appraisal.period}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Overall rating {appraisal.overallRating}/5
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {Object.entries(appraisal.categories || {}).map(([key, value]) => (
                      <Box key={key} mb={1.5}>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{key}</Typography>
                        <Rating size="small" value={Number(value || 0)} readOnly />
                      </Box>
                    ))}
                    <Typography variant="body2">{appraisal.reflection}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <ManagerIcon sx={{ color: THEME_COLORS.primary }} />
                <Typography variant="h6" fontWeight="bold">Manager appraisals</Typography>
              </Box>

              {managerAppraisals.length === 0 && (
                <Typography color="text.secondary">
                  Manager appraisal records will appear here when they are added to the user record.
                </Typography>
              )}
              {managerAppraisals.map((appraisal) => (
                <Accordion key={appraisal.id} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box>
                      <Typography fontWeight="bold">{appraisal.period}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Appraiser: {appraisal.appraiserName || 'Manager'}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2">{appraisal.developmentPlan || appraisal.feedback || 'No detail recorded.'}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={goalDialogOpen} onClose={() => setGoalDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add development goal</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Goal title" value={goalForm.title} onChange={(e) => setGoalForm((prev) => ({ ...prev, title: e.target.value }))} fullWidth />
            <TextField label="Description" value={goalForm.description} onChange={(e) => setGoalForm((prev) => ({ ...prev, description: e.target.value }))} fullWidth multiline rows={3} />
            <TextField label="Priority" value={goalForm.priority} onChange={(e) => setGoalForm((prev) => ({ ...prev, priority: e.target.value }))} fullWidth />
            <TextField label="Target date" type="date" value={goalForm.targetDate} onChange={(e) => setGoalForm((prev) => ({ ...prev, targetDate: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField label="Success measure" value={goalForm.successMeasure} onChange={(e) => setGoalForm((prev) => ({ ...prev, successMeasure: e.target.value }))} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoalDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleGoalSubmit} disabled={saving || !goalForm.title.trim()} variant="contained" sx={{ bgcolor: THEME_COLORS.primary }}>
            Save goal
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={activityDialogOpen} onClose={() => setActivityDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Log professional activity</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Activity title" value={activityForm.title} onChange={(e) => setActivityForm((prev) => ({ ...prev, title: e.target.value }))} fullWidth />
            <TextField label="Activity type" value={activityForm.type} onChange={(e) => setActivityForm((prev) => ({ ...prev, type: e.target.value }))} fullWidth />
            <TextField label="Duration (hours)" type="number" value={activityForm.duration} onChange={(e) => setActivityForm((prev) => ({ ...prev, duration: e.target.value }))} fullWidth />
            <TextField label="Date" type="date" value={activityForm.date} onChange={(e) => setActivityForm((prev) => ({ ...prev, date: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField label="Reflection" value={activityForm.reflection} onChange={(e) => setActivityForm((prev) => ({ ...prev, reflection: e.target.value }))} fullWidth multiline rows={3} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivityDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleActivitySubmit} disabled={saving || !activityForm.title.trim()} variant="contained" sx={{ bgcolor: THEME_COLORS.primary }}>
            Save activity
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={appraisalDialogOpen} onClose={() => setAppraisalDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New self appraisal</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Review period" value={appraisalForm.period} onChange={(e) => setAppraisalForm((prev) => ({ ...prev, period: e.target.value }))} fullWidth />
            {Object.entries(appraisalForm.categories).map(([key, value]) => (
              <Box key={key}>
                <Typography variant="body2" sx={{ textTransform: 'capitalize', mb: 0.5 }}>{key}</Typography>
                <Rating
                  value={Number(value)}
                  onChange={(_, newValue) => setAppraisalForm((prev) => ({
                    ...prev,
                    categories: {
                      ...prev.categories,
                      [key]: newValue || 0
                    }
                  }))}
                />
              </Box>
            ))}
            <TextField label="Reflection" value={appraisalForm.reflection} onChange={(e) => setAppraisalForm((prev) => ({ ...prev, reflection: e.target.value }))} fullWidth multiline rows={3} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAppraisalDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAppraisalSubmit} disabled={saving || !appraisalForm.period.trim()} variant="contained" sx={{ bgcolor: THEME_COLORS.primary }}>
            Save appraisal
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfessionalDevelopmentGoals;
